import { ElementRef, Injectable } from '@angular/core';
import { AutoComplete } from 'primeng/autocomplete';
import { Quotation } from '../../../modals/product.modals';

/**
 * Shared dependency bag for the keyboard-navigation cluster.
 *
 * These methods were extracted as a single cluster (rather than one-by-one)
 * because they call each other directly (e.g. handleKeyboardShortcuts ->
 * onGlobalKeyDown, handleEscapeKey, onTransactionHistoryKeyDown, ...). The
 * component builds one of these per entry point via a private helper and
 * passes it straight through so every method in the cluster sees identical
 * state to what `this.x` would have read synchronously in the original code.
 */
export interface KeyboardNavigationDeps {
  // Dialog / mode state (read-only within this cluster)
  userName: string;
  displayModal: boolean;
  showTransactionDialog: boolean;
  displayHoldNameDialog: boolean;
  showOfferGroupCustomerDialog: boolean;
  savedReceiptsDialog: boolean;
  showCashInDialog: boolean;
  showTotalDialog: boolean;
  showInvoiceDialog: boolean;
  showKeyboardHelp: boolean;
  displayDialog: boolean;
  splitBillDialog: boolean;
  showItemDetailDialog: boolean;
  showDescriptionDialog: boolean;
  showWarrantyDialog: boolean;
  showQuotationOptionsDialog: boolean;
  displayMobileNumberDialog: boolean;
  isDeleteMode: boolean;
  hasScannerClicked: boolean;
  activeHistoryTab: number;
  flattenedTransactions: any[];
  quotations: Quotation[];
  selectedTransactionIndex: number;
  selectedQuotationIndex: number;
  customerMobile: string;
  offerGroupMobileAutocomplete: AutoComplete;
  dashboardContainer: ElementRef;
  enableCursorFocus: boolean;
  isQuotationMode: boolean;
  isProcessingQuotation: boolean;

  // Live getters (NOT snapshots) for setupGlobalKeyboardShortcuts' deferred
  // addEventListener callbacks, which must read the current value whenever
  // the browser fires them, not the value at setup time.
  getUserName: () => string;
  getEnableCursorFocus: () => boolean;

  // Setters for the handful of fields this cluster mutates
  setShowTransactionDialog: (value: boolean) => void;
  setDisplayHoldNameDialog: (value: boolean) => void;
  setShowOfferGroupCustomerDialog: (value: boolean) => void;
  setSavedReceiptsDialog: (value: boolean) => void;
  setShowKeyboardHelp: (value: boolean) => void;
  setSelectedTransactionIndex: (value: number) => void;
  setSelectedQuotationIndex: (value: number) => void;

  // Cross-cutting component methods this cluster calls but does not own
  handleEvent: (key: string) => void;
  closeTotalDialog: () => void;
  closeItemDetailDialog: () => void;
  closeDescriptionDialog: () => void;
  closeWarrantyDialog: () => void;
  toggleQR: () => void;
  focusBarcodeSearch: () => void;
  focusProductSearch: () => void;
  focusOnDiscountToggle: () => void;
  toggleQuotationMode: () => void;
  toggleSuspenseMode: () => void;
  toggleWholesaleMode: () => void;
  toggleOfferGroupMode: () => void;
  canCompleteQuotation: () => boolean;
  processQuotation: () => void;
  canCompletePayment: () => boolean;
  processPayment: () => void;
  switchHistoryTab: (goNext: boolean) => void;
  printReceipt: (canGenerateInvoice: boolean) => void;
  editQuotation: (quotation: Quotation) => void;
  selectTransactionByIndex: (index: number) => void;
  selectQuotationByIndex: (index: number) => void;
  proceedWithCustomer: () => void;
  proceedWithoutCustomer: () => void;
}

/**
 * KeyboardNavigationService
 *
 * Dedicated service for the dashboard's global keyboard-shortcut handling,
 * dialog focus-trapping, and history/quotation list keyboard navigation.
 *
 * Extracted from DashboardComponent to decouple keyboard-handling logic
 * while preserving 100% identical behavior.
 */
@Injectable({
  providedIn: 'root',
})
export class KeyboardNavigationService {
  handleKeyboardShortcuts(event: KeyboardEvent, deps: KeyboardNavigationDeps) {
    if (!deps.userName || deps.displayModal) {
      return;
    }

    // Don't interfere with input fields or when dialogs are open
    const target = event.target as HTMLElement;
    const isInputField =
      target.tagName === 'INPUT' ||
      target.tagName === 'TEXTAREA' ||
      target.contentEditable === 'true';

    if (event.key === 'Tab') {
      if (
        !deps.showTransactionDialog &&
        !deps.displayHoldNameDialog &&
        !deps.showOfferGroupCustomerDialog &&
        !deps.savedReceiptsDialog
      ) {
        return;
      }
    }

    if (event.key === 'Enter' && isInputField) {
      return; // Let default behavior handle these
    }

    if (event.ctrlKey && ['1', '2', '3'].includes(event.key)) {
      this.onGlobalKeyDown(event, deps);
      return;
    }

    if (
      event.altKey &&
      ['q', 'Q', 'w', 'W', 'm', 'M', 'o', 'O'].includes(event.key)
    ) {
      this.onGlobalKeyDown(event, deps);
      return;
    }

    if (event.ctrlKey || event.altKey) {
      return;
    }

    // Block ALL F-keys when ANY dialog is open - only one dialog at a time
    const hasOpenDialog =
      deps.showCashInDialog ||
      deps.showTotalDialog ||
      deps.showInvoiceDialog ||
      deps.showTransactionDialog ||
      deps.displayHoldNameDialog ||
      deps.savedReceiptsDialog ||
      deps.showKeyboardHelp ||
      deps.showOfferGroupCustomerDialog ||
      deps.displayDialog ||
      deps.splitBillDialog ||
      deps.showItemDetailDialog ||
      deps.showDescriptionDialog ||
      deps.showWarrantyDialog ||
      deps.showQuotationOptionsDialog ||
      deps.displayMobileNumberDialog ||
      deps.isDeleteMode; // Block F-keys when delete mode is active

    if (
      hasOpenDialog &&
      [
        'F1',
        'F2',
        'F3',
        'F4',
        'F5',
        'F6',
        'F7',
        'F8',
        'F9',
        'F10',
        'F11',
        'F12',
      ].includes(event.key)
    ) {
      // Allow F9 to pass through even in delete mode (to toggle delete mode off)
      if (event.key === 'F9' && deps.isDeleteMode) {
        // Let F9 pass through to toggle delete mode
      } else {
        event.preventDefault();
        return; // Block all other F-keys when any dialog is open or delete mode is active
      }
    }

    switch (event.key) {
      case 'F1':
        event.preventDefault();
        deps.handleEvent('F1');
        break;
      case 'F2':
        event.preventDefault();
        deps.handleEvent('F2');
        break;
      case 'F3':
        event.preventDefault();
        deps.handleEvent('F3');
        break;
      case 'F4':
        event.preventDefault();
        deps.handleEvent('F4');
        break;
      case 'F5':
        event.preventDefault();
        deps.handleEvent('F5');
        break;
      case 'F6':
        event.preventDefault();
        deps.handleEvent('F6');
        break;
      case 'F7':
        event.preventDefault();
        deps.handleEvent('F7');
        break;
      case 'F8':
        event.preventDefault();
        deps.handleEvent('F8');
        break;
      case 'F9':
        // F9 is handled by layout component via customEvent$ subscription
        // Don't handle it here to avoid double-triggering
        break;
      // F10, F11, F12 are handled ENTIRELY by layout component
      // Don't prevent default here - let layout component handle it first (window:keydown fires before document:keydown)
      case 'F10':
        break;
      case 'F11':
        break;
      case 'F12':
        break;
      case 'Escape':
        this.handleEscapeKey(deps);
        break;
      case 'Tab':
        if (deps.displayHoldNameDialog) {
          // this.handleHoldDialogTabNavigation(event);
        } else if (deps.showOfferGroupCustomerDialog) {
          this.handleOfferGroupDialogTabNavigation(event, deps);
        } else if (deps.showTransactionDialog) {
          this.handleHistoryDialogTabNavigation(event);
        } else if (deps.savedReceiptsDialog) {
          // this.handleResumeReceiptDialogTabNavigation(event);
        }
        break;
      case '?':
        if (event.shiftKey) {
          // Shift + ? = show help
          event.preventDefault();
          this.toggleKeyboardHelp(deps);
        }
        break;
      case 'ArrowDown':
      case 'ArrowUp':
        if (deps.showTransactionDialog) {
          event.preventDefault();
          this.onTransactionHistoryKeyDown(event, deps);
        }
        break;
      case 'ArrowLeft':
      case 'ArrowRight':
        if (deps.showTransactionDialog && event.ctrlKey) {
          event.preventDefault();
          this.onTransactionHistoryKeyDown(event, deps);
        }
        break;
      case 'Enter':
        if (deps.showTransactionDialog) {
          event.preventDefault();
          this.onTransactionHistoryKeyDown(event, deps);
        }
        break;
    }
  }

  handleEscapeKey(deps: KeyboardNavigationDeps) {
    // Close any open dialogs
    if (deps.showKeyboardHelp) {
      deps.setShowKeyboardHelp(false);
    } else if (deps.showTotalDialog) {
      deps.closeTotalDialog();
    } else if (deps.showInvoiceDialog) {
      // this.hideDialog();
    } else if (deps.showTransactionDialog) {
      deps.setShowTransactionDialog(false);
    } else if (deps.displayHoldNameDialog) {
      deps.setDisplayHoldNameDialog(false);
    } else if (deps.showOfferGroupCustomerDialog) {
      deps.setShowOfferGroupCustomerDialog(false);
    } else if (deps.savedReceiptsDialog) {
      // this.hideSavedReceiptsDialog();
      deps.setSavedReceiptsDialog(false);
    } else if (deps.showWarrantyDialog) {
      // Item dialogs are closable=false, and PrimeNG only honours closeOnEscape on closable dialogs
      deps.closeWarrantyDialog();
    } else if (deps.showDescriptionDialog) {
      deps.closeDescriptionDialog();
    } else if (deps.showItemDetailDialog) {
      deps.closeItemDetailDialog();
    } else if (deps.hasScannerClicked) {
      deps.toggleQR();
    }
  }

  toggleKeyboardHelp(deps: KeyboardNavigationDeps) {
    deps.setShowKeyboardHelp(!deps.showKeyboardHelp);
  }

  // Global keyboard shortcuts
  onGlobalKeyDown(event: KeyboardEvent, deps: KeyboardNavigationDeps) {
    // Handle Ctrl+1, Ctrl+2, and Ctrl+3 shortcuts
    if (event.ctrlKey) {
      switch (event.key) {
        case '1':
          event.preventDefault();
          deps.focusBarcodeSearch();
          break;
        case '2':
          event.preventDefault();
          deps.focusProductSearch();
          break;
        case '3':
          event.preventDefault();
          deps.focusOnDiscountToggle();
          break;
      }
    }

    // Handle Alt+Q for Quotation Mode toggle (only when no dialogs are open)
    if (event.altKey && (event.key === 'q' || event.key === 'Q')) {
      // Don't handle Alt+Q if any dialog is open (let dialog handle its own Alt+Q)
      const hasOpenDialog =
        deps.showTotalDialog ||
        deps.showInvoiceDialog ||
        deps.showTransactionDialog ||
        deps.displayHoldNameDialog ||
        deps.savedReceiptsDialog ||
        deps.showKeyboardHelp;

      if (!hasOpenDialog) {
        event.preventDefault();
        event.stopPropagation();
        deps.toggleQuotationMode();
      }
    }

    // Handle Alt+M for Suspense Account Mode toggle (only when no dialogs are open)
    if (event.altKey && (event.key === 'm' || event.key === 'M')) {
      // Don't handle Alt+M if any dialog is open
      const hasOpenDialog =
        deps.showTotalDialog ||
        deps.showInvoiceDialog ||
        deps.showTransactionDialog ||
        deps.displayHoldNameDialog ||
        deps.savedReceiptsDialog ||
        deps.showKeyboardHelp;

      if (!hasOpenDialog) {
        event.preventDefault();
        event.stopPropagation();
        deps.toggleSuspenseMode();
      }
    }

    // Handle Alt+W for Wholesale Mode toggle
    if (event.altKey && (event.key === 'w' || event.key === 'W')) {
      event.preventDefault();
      event.stopPropagation();
      deps.toggleWholesaleMode();
    }

    // Handle Alt+O for Offer Group Mode toggle (only when no dialogs are open)
    if (event.altKey && (event.key === 'o' || event.key === 'O')) {
      const hasOpenDialog =
        deps.showTotalDialog ||
        deps.showInvoiceDialog ||
        deps.showTransactionDialog ||
        deps.displayHoldNameDialog ||
        deps.savedReceiptsDialog ||
        deps.showKeyboardHelp ||
        deps.showOfferGroupCustomerDialog;

      if (!hasOpenDialog) {
        event.preventDefault();
        event.stopPropagation();
        deps.toggleOfferGroupMode();
      }
    }
  }

  onPaymentDialogKeyDown(
    event: KeyboardEvent,
    deps: KeyboardNavigationDeps,
  ): void {
    const target = event.target as HTMLElement;

    if (event.key === 'Enter') {
      if (target.tagName === 'BUTTON') {
        return;
      }

      if (target.classList.contains('p-autocomplete-input')) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      if (deps.isQuotationMode) {
        if (deps.canCompleteQuotation() && !deps.isProcessingQuotation) {
          deps.processQuotation();
        }
      } else {
        if (deps.canCompletePayment()) {
          deps.processPayment();
        }
      }
      return;
    }

    if (event.key === ' ' && target.tagName === 'BUTTON') {
      return;
    }

    if (event.altKey && (event.key === 'q' || event.key === 'Q')) {
      event.preventDefault();
      event.stopPropagation();
      deps.toggleQuotationMode();
    }
    if (event.altKey && (event.key === 'w' || event.key === 'W')) {
      event.preventDefault();
      event.stopPropagation();
      deps.toggleWholesaleMode();
    }
  }

  // Transaction History keyboard navigation
  onTransactionHistoryKeyDown(
    event: KeyboardEvent,
    deps: KeyboardNavigationDeps,
  ) {
    if (!deps.showTransactionDialog) {
      return;
    }

    // Handle tab switching with Ctrl+Left/Right
    if (
      event.ctrlKey &&
      (event.key === 'ArrowLeft' || event.key === 'ArrowRight')
    ) {
      event.preventDefault();
      deps.switchHistoryTab(event.key === 'ArrowRight');
      return;
    }

    // Handle navigation based on active tab
    if (deps.activeHistoryTab === 0 && deps.flattenedTransactions.length === 0) {
      return;
    }
    if (deps.activeHistoryTab === 1 && deps.quotations.length === 0) {
      return;
    }

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (deps.activeHistoryTab === 0) {
          this.navigateTransactionDown(deps);
        } else {
          this.navigateQuotationDown(deps);
        }
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (deps.activeHistoryTab === 0) {
          this.navigateTransactionUp(deps);
        } else {
          this.navigateQuotationUp(deps);
        }
        break;
      case 'Enter':
        event.preventDefault();
        if (
          deps.activeHistoryTab === 0 &&
          deps.selectedTransactionIndex >= 0 &&
          deps.flattenedTransactions.length > 0
        ) {
          deps.printReceipt(false);
        } else if (
          deps.activeHistoryTab === 1 &&
          deps.selectedQuotationIndex >= 0 &&
          deps.quotations.length > 0
        ) {
          const selectedQuotation = deps.quotations[deps.selectedQuotationIndex];
          if (selectedQuotation) {
            deps.editQuotation(selectedQuotation);
          }
        }
        break;
    }
  }

  navigateTransactionDown(deps: KeyboardNavigationDeps) {
    if (deps.selectedTransactionIndex < deps.flattenedTransactions.length - 1) {
      deps.setSelectedTransactionIndex(deps.selectedTransactionIndex + 1);
      deps.selectTransactionByIndex(deps.selectedTransactionIndex + 1);
    }
  }

  navigateTransactionUp(deps: KeyboardNavigationDeps) {
    if (deps.selectedTransactionIndex > 0) {
      deps.setSelectedTransactionIndex(deps.selectedTransactionIndex - 1);
      deps.selectTransactionByIndex(deps.selectedTransactionIndex - 1);
    }
  }

  // Quotation History keyboard navigation methods
  navigateQuotationDown(deps: KeyboardNavigationDeps) {
    if (deps.selectedQuotationIndex < deps.quotations.length - 1) {
      deps.setSelectedQuotationIndex(deps.selectedQuotationIndex + 1);
      deps.selectQuotationByIndex(deps.selectedQuotationIndex + 1);
    }
  }

  navigateQuotationUp(deps: KeyboardNavigationDeps) {
    if (deps.selectedQuotationIndex > 0) {
      deps.setSelectedQuotationIndex(deps.selectedQuotationIndex - 1);
      deps.selectQuotationByIndex(deps.selectedQuotationIndex - 1);
    }
  }

  onExchangeQuantityTab(event: KeyboardEvent, currentIndex: number): void {
    if (event.key !== 'Tab') return;

    const exchangeInputs = document.querySelectorAll(
      '.exchange-qty-input',
    ) as NodeListOf<HTMLInputElement>;

    if (exchangeInputs.length > 1 && currentIndex < exchangeInputs.length - 1) {
      event.preventDefault();
      const nextInput = exchangeInputs[currentIndex + 1];
      if (nextInput) {
        nextInput.focus();
        nextInput.select();
      }
    }
  }

  onQuantityInputTab(event: Event, currentIndex: number): void {
    event.preventDefault();
    const discountInputs = document.querySelectorAll(
      'input[placeholder="Discount"]',
    ) as NodeListOf<HTMLInputElement>;

    if (discountInputs[currentIndex]) {
      discountInputs[currentIndex].focus();
      discountInputs[currentIndex].select();
    }
  }

  onAmountInputTab(event: KeyboardEvent, currentIndex: number): void {
    if (event.key !== 'Tab') return;

    event.preventDefault();
    const removeButtons = document.querySelectorAll(
      '.delete-item-btn',
    ) as NodeListOf<HTMLButtonElement>;

    if (removeButtons[currentIndex]) {
      removeButtons[currentIndex].focus();
    }
  }

  handleOfferGroupDialogTabNavigation(
    event: KeyboardEvent,
    deps: KeyboardNavigationDeps,
  ) {
    const continueButton = document.querySelector(
      '#offerGroupContinueBtn',
    ) as HTMLButtonElement;
    const skipButton = document.querySelector(
      '#offerGroupSkipBtn',
    ) as HTMLButtonElement;

    // Get the autocomplete input element
    let mobileInput: HTMLInputElement | null = null;
    if (deps.offerGroupMobileAutocomplete && deps.offerGroupMobileAutocomplete.el) {
      mobileInput =
        deps.offerGroupMobileAutocomplete.el.nativeElement.querySelector(
          'input',
        );
    }

    const target = event.target as HTMLElement;

    // Check if we're on the autocomplete input
    if (target && target.classList.contains('p-autocomplete-input')) {
      // Allow autocomplete dropdown navigation
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        return;
      }

      if (event.key === 'Tab' && !event.shiftKey) {
        const panel = document.querySelector('.p-autocomplete-panel');
        if (panel && panel.classList.contains('p-connected-overlay-visible')) {
          return;
        }

        event.preventDefault();
        event.stopPropagation();

        if (continueButton && continueButton.disabled) {
          skipButton?.focus();
        } else {
          continueButton?.focus();
        }
        return;
      }

      if (event.key === 'Enter') {
        const panel = document.querySelector('.p-autocomplete-panel');
        if (panel && panel.classList.contains('p-connected-overlay-visible')) {
          return;
        }
        if (deps.customerMobile && deps.customerMobile.length === 10) {
          event.preventDefault();
          deps.proceedWithCustomer();
          return;
        }
      }
      return;
    }

    if (event.key !== 'Tab' && event.key !== 'Enter') return;

    if (document.activeElement === continueButton) {
      if (event.key === 'Tab') {
        event.preventDefault();
        event.stopPropagation();
        if (event.shiftKey) {
          mobileInput?.focus();
        } else {
          skipButton?.focus();
        }
        return;
      }
      if (
        event.key === 'Enter' &&
        deps.customerMobile &&
        deps.customerMobile.length === 10
      ) {
        event.preventDefault();
        deps.proceedWithCustomer();
        return;
      }
    }

    if (document.activeElement === skipButton) {
      if (event.key === 'Tab') {
        event.preventDefault();
        event.stopPropagation();
        if (event.shiftKey) {
          // If continue button is disabled, skip directly to mobile input
          if (continueButton && continueButton.disabled) {
            mobileInput?.focus();
          } else {
            continueButton?.focus();
          }
        } else {
          mobileInput?.focus();
        }
        return;
      }
      if (event.key === 'Enter') {
        event.preventDefault();
        deps.proceedWithoutCustomer();
        return;
      }
    }
  }

  handleHistoryDialogTabNavigation(event: KeyboardEvent) {
    if (event.key !== 'Tab') return;

    event.preventDefault();
    event.stopPropagation();

    // Get the dialog element - try multiple selectors
    const dialog =
      document.querySelector('.premium-history-dialog .p-dialog') ||
      document.querySelector('.premium-history-dialog') ||
      document.querySelector('p-dialog[ng-reflect-visible="true"]');

    if (!dialog) {
      console.warn('History dialog not found');
      return;
    }

    // Get all focusable elements within the dialog
    const focusableElements = dialog.querySelectorAll(
      'a[href]:not([disabled]):not([tabindex="-1"]), ' +
      'button:not([disabled]):not([tabindex="-1"]), ' +
      'input:not([disabled]):not([tabindex="-1"]), ' +
      'select:not([disabled]):not([tabindex="-1"]), ' +
      'textarea:not([disabled]):not([tabindex="-1"]), ' +
      '[tabindex]:not([tabindex="-1"]):not([disabled])',
    ) as NodeListOf<HTMLElement>;

    if (focusableElements.length === 0) {
      console.warn('No focusable elements found in dialog');
      return;
    }

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];
    const activeElement = document.activeElement as HTMLElement;

    // Check if active element is within the dialog
    const isActiveInDialog = dialog.contains(activeElement);

    if (!isActiveInDialog) {
      // If focus is outside dialog, focus the first element
      firstElement.focus();
      return;
    }

    // If Shift+Tab on first element, go to last element
    if (event.shiftKey && activeElement === firstElement) {
      lastElement.focus();
      return;
    }

    // If Tab on last element, go to first element
    if (!event.shiftKey && activeElement === lastElement) {
      firstElement.focus();
      return;
    }

    // Otherwise, move to next/previous focusable element
    const currentIndex = Array.from(focusableElements).indexOf(activeElement);
    if (currentIndex !== -1) {
      const nextIndex = event.shiftKey ? currentIndex - 1 : currentIndex + 1;
      if (nextIndex >= 0 && nextIndex < focusableElements.length) {
        focusableElements[nextIndex].focus();
      }
    } else {
      // If current element not found in list, focus the first one
      firstElement.focus();
    }
  }

  setupGlobalKeyboardShortcuts(deps: KeyboardNavigationDeps): void {
    if (!deps.dashboardContainer) {
      return;
    }

    const container = deps.dashboardContainer.nativeElement;

    window.addEventListener('focus', () => {
      if (deps.getUserName() && !deps.getEnableCursorFocus()) {
        setTimeout(() => {
          container.focus();
        }, 50);
      }
    });

    container.addEventListener('click', (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const isInputElement =
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.tagName === 'BUTTON' ||
        target.contentEditable === 'true';

      if (!isInputElement && deps.getUserName()) {
        container.focus();
      }
    });

    if (deps.userName && !deps.enableCursorFocus) {
      setTimeout(() => {
        container.focus();
      }, 100);
    }
  }
}
